import {
    getFirestore, collection, query, orderBy, limit, startAfter,
    getDocs, getDoc, doc, updateDoc, arrayUnion, where,
    getCountFromServer, endBefore, limitToLast
} from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);
const USERS_COLLECTION = "users";
export const PAGE_SIZE = 20;

export const AdminUserService = {
    /**
     * Fetch users with cursor-based pagination
     * @param {Object|null} lastCursor - The doc snapshot to start after (for Next)
     * @param {Object|null} prevCursor - The doc snapshot to end before (for Prev - requires different query strategy or stack)
     * @param {string|null} searchEmail - Optional email prefix to search
     * @returns {Promise<{users: Array, lastVisible: Object|null, totalCount: number}>}
     */
    fetchUsers: async (lastCursor = null, direction = 'next', searchEmail = null) => {
        try {
            let q = collection(db, USERS_COLLECTION);

            // 1. Base Query construction
            let constraints = [];

            // Search Logic (Prefix Match)
            if (searchEmail) {
                // Firestore Prefix Search: where('email', '>=', term) and where('email', '<=', term + '\uf8ff')
                constraints.push(orderBy("email"));
                constraints.push(where("email", ">=", searchEmail));
                constraints.push(where("email", "<=", searchEmail + '\uf8ff'));
            } else {
                // Default Order
                constraints.push(orderBy("createdAt", "desc")); // Default to newest users
            }

            // count query (only needed on first load or refresh, but we do it nicely)
            // optimization: separate count function to call only when needed? 
            // For now, let's just return totalCount if it's the first page or we want to show it.

            // Pagination Logic
            if (direction === 'next' && lastCursor) {
                constraints.push(startAfter(lastCursor));
            }

            // Note: 'Previous' in Firestore is tricky without keeping a stack of cursors.
            // We will rely on the UI managing the cursor stack for 'Previous'. 
            // If we strictly used 'endBefore', we'd need to reverse logic. 
            // Simpler approach for this admin implementation: The UI keeps an array of 'starting cursors' for each page.

            constraints.push(limit(PAGE_SIZE));

            const finalQuery = query(q, ...constraints);
            const snapshot = await getDocs(finalQuery);

            const users = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

            return {
                users,
                lastVisible,
                loading: false
            };

        } catch (error) {
            console.error("AdminUserService.fetchUsers Error:", error);
            throw error;
        }
    },

    /**
     * Get total user count for pagination UI
     */
    getTotalUserCount: async (searchEmail = null) => {
        try {
            let q = collection(db, USERS_COLLECTION);
            if (searchEmail) {
                const constraints = [
                    orderBy("email"),
                    where("email", ">=", searchEmail),
                    where("email", "<=", searchEmail + '\uf8ff')
                ];
                q = query(q, ...constraints);
            }
            const snapshot = await getCountFromServer(q);
            return snapshot.data().count;
        } catch (error) {
            console.error("Error getting count:", error);
            return 0;
        }
    },

    /**
     * Update User Role
     */
    updateRole: async (uid, newRole) => {
        try {
            const userRef = doc(db, USERS_COLLECTION, uid);
            await updateDoc(userRef, { role: newRole });
            return true;
        } catch (error) {
            console.error("Error updating role:", error);
            throw error;
        }
    },

    /**
     * Award Badge
     */
    awardBadge: async (uid, badgeId) => {
        try {
            const userRef = doc(db, USERS_COLLECTION, uid);
            await updateDoc(userRef, {
                badges: arrayUnion(badgeId)
            });
            return true;
        } catch (error) {
            console.error("Error awarding badge:", error);
            throw error;
        }
    },



    /**
     * Unban User (Restore Status)
     */
    unbanUser: async (uid) => {
        try {
            const userRef = doc(db, USERS_COLLECTION, uid);
            await updateDoc(userRef, {
                status: 'active', // Restore to active
                bannedAt: null    // Clear ban timestamp
            });
            return true;
        } catch (error) {
            console.error("Error unbanning user:", error);
            throw error;
        }
    }
};
